import { useCallback, useEffect, useRef, useState } from 'react';
import { audioBus } from '@/lib/audioBus';

export const PREVIEW_SECONDS = 10;
const HOVER_INTENT_MS = 220;
const FADE_IN_MS = 450;
const FADE_OUT_MS = 600;
const MAX_VOL = 0.85;

/** Same rule the admin editor shows as "Auto". */
export const autoPreviewStart = (d?: number | null) => (d && d > 40 ? Math.round(d * 0.3) : 0);

export interface PreviewTarget {
  id: string;
  url: string;
  duration: number;
  start?: number | null;
}

export interface PreviewState {
  id: string;
  status: 'idle' | 'loading' | 'playing';
  pos: number;
  from: number;
  to: number;
}

const IDLE: PreviewState = { id: '', status: 'idle', pos: 0, from: 0, to: 0 };

/**
 * Hover previews: plays a short faded snippet of a track on its own <audio> element,
 * so the main player keeps its position. Browsers only allow sound after the visitor
 * has interacted with the page once; `onBlocked` fires if a preview is refused.
 */
export function useHoverPreview(onBlocked?: () => void) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<PreviewState>(IDLE);
  const token = useRef(0);
  const intent = useRef<number | undefined>(undefined);
  const raf = useRef<number | undefined>(undefined);
  const win = useRef({ from: 0, to: 0, fadeStart: 0, stopping: false });
  const blockedRef = useRef(onBlocked);
  blockedRef.current = onBlocked;

  const hardStop = useCallback(() => {
    token.current++;
    window.clearTimeout(intent.current);
    if (raf.current) cancelAnimationFrame(raf.current);
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.volume = 0;
    }
    setState(IDLE);
  }, []);

  useEffect(() => {
    const off = audioBus.on((owner) => owner !== 'preview' && hardStop());
    return () => {
      off();
      hardStop();
    };
  }, [hardStop]);

  const loop = useCallback((my: number) => {
    const a = audioRef.current;
    if (!a || my !== token.current) return;
    const now = performance.now();
    const w = win.current;
    if (w.stopping) {
      const k = 1 - (now - w.fadeStart) / FADE_OUT_MS;
      if (k <= 0) {
        a.pause();
        setState(IDLE);
        return;
      }
      a.volume = Math.max(0, Math.min(a.volume, MAX_VOL * k));
    } else {
      const k = Math.min(1, (now - w.fadeStart) / FADE_IN_MS);
      a.volume = MAX_VOL * k;
      if (a.currentTime >= w.to - FADE_OUT_MS / 1000 || a.ended) {
        w.stopping = true;
        w.fadeStart = now;
      }
    }
    setState((s) => (s.status === 'idle' ? s : { ...s, pos: a.currentTime }));
    raf.current = requestAnimationFrame(() => loop(my));
  }, []);

  const start = useCallback(
    (t: PreviewTarget) => {
      if (!t.url) return;
      window.clearTimeout(intent.current);
      const my = ++token.current;
      intent.current = window.setTimeout(() => {
        if (my !== token.current) return;
        let a = audioRef.current;
        if (!a) {
          a = new Audio();
          a.preload = 'auto';
          audioRef.current = a;
        }
        const dur = t.duration || 0;
        const from = Math.max(0, Math.min(t.start ?? autoPreviewStart(dur), dur ? Math.max(0, dur - 2) : Infinity));
        const to = dur ? Math.min(dur, from + PREVIEW_SECONDS) : from + PREVIEW_SECONDS;
        win.current = { from, to, fadeStart: performance.now(), stopping: false };
        setState({ id: t.id, status: 'loading', pos: from, from, to });
        audioBus.claim('preview');
        const el = a;
        const go = () => {
          if (my !== token.current) return;
          try {
            el.currentTime = from;
          } catch {
            /* not seekable yet */
          }
          el.volume = 0;
          el.play()
            .then(() => {
              if (my !== token.current) return el.pause();
              win.current.fadeStart = performance.now();
              setState((s) => (s.id === t.id ? { ...s, status: 'playing' } : s));
              if (raf.current) cancelAnimationFrame(raf.current);
              raf.current = requestAnimationFrame(() => loop(my));
            })
            .catch((err) => {
              if (my !== token.current) return;
              setState(IDLE);
              if (err && err.name === 'NotAllowedError') blockedRef.current?.();
            });
        };
        if (el.getAttribute('src') !== t.url) {
          el.src = t.url;
          el.addEventListener('loadedmetadata', go, { once: true });
          el.load();
        } else if (el.readyState >= 1) go();
        else el.addEventListener('loadedmetadata', go, { once: true });
      }, HOVER_INTENT_MS);
    },
    [loop],
  );

  /** Fade out the current preview (or cancel a pending one). */
  const stop = useCallback(
    (id?: string) => {
      window.clearTimeout(intent.current);
      setState((s) => {
        if (id && s.id && s.id !== id) return s;
        if (s.status === 'idle') return s;
        if (s.status === 'loading') {
          token.current++;
          audioRef.current?.pause();
          return IDLE;
        }
        win.current.stopping = true;
        win.current.fadeStart = performance.now();
        return s;
      });
    },
    [],
  );

  return { preview: state, startPreview: start, stopPreview: stop, cancelPreview: hardStop };
}
